import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, Mail, Zap } from 'lucide-react'
import { useSpring, animated } from '@react-spring/web'
import { toast } from 'sonner'

import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { useAuth } from '@/hooks/useAuth'
import AuthService from '@/services/auth.service'
import { useBrandingStore } from '@/stores/brandingStore'

interface LoginFormData {
  email: string
  password: string
  remember_me: boolean
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const navigate = useNavigate()
  const { login, isLoading } = useAuth()
  const platformName = useBrandingStore((state) => state.platform_name)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { remember_me: false },
  })

  const cardSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 280, friction: 26 },
  })

  const logoSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.8)' },
    to: { opacity: 1, transform: 'scale(1)' },
    config: { tension: 280, friction: 26 },
    delay: 100,
  })

  async function onSubmit(data: LoginFormData) {
    try {
      await login(data.email, data.password)
      navigate('/dashboard', { replace: true })
    } catch {
      // Toast handled in auth store
    }
  }

  async function handleGoogleLogin() {
    setIsGoogleLoading(true)
    try {
      await AuthService.loginWithGoogle()
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : 'Nao foi possivel iniciar o login com Google.'
      toast.error(message)
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center p-4">
      <animated.div style={cardSpring} className="w-full max-w-md">
        <animated.div style={logoSpring} className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[var(--color-brand)] to-[var(--color-secondary)] shadow-[var(--shadow-glow)]">
            <Zap size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            Bem-vindo ao <span className="gradient-text">{platformName || 'ISing'}</span>
          </h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">Faca login para continuar</p>
        </animated.div>

        <div className="glass-strong p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <Input
              label="E-mail"
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
              leftIcon={<Mail size={16} />}
              error={errors.email?.message}
              {...register('email', {
                required: 'E-mail e obrigatorio',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'E-mail invalido',
                },
              })}
            />

            <Input
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              leftIcon={<Lock size={16} />}
              rightIcon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              onRightIconClick={() => setShowPassword((value) => !value)}
              error={errors.password?.message}
              {...register('password', {
                required: 'Senha e obrigatoria',
                minLength: {
                  value: 6,
                  message: 'Senha deve ter pelo menos 6 caracteres',
                },
              })}
            />

            <div className="flex items-center justify-between">
              <label className="group flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer rounded accent-[var(--color-brand)]"
                  {...register('remember_me')}
                />
                <span className="text-sm text-[var(--text-secondary)] transition-colors duration-150 group-hover:text-[var(--text-primary)]">
                  Lembrar de mim
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-[var(--color-brand)] transition-colors duration-150 hover:text-[var(--color-brand-hover)]"
              >
                Esqueceu a senha?
              </Link>
            </div>

            <Button type="submit" fullWidth loading={isLoading} size="lg">
              Entrar
            </Button>

            <div className="relative flex items-center gap-3">
              <div className="h-px flex-1 bg-[var(--border-medium)]" />
              <span className="text-xs text-[var(--text-muted)]">ou</span>
              <div className="h-px flex-1 bg-[var(--border-medium)]" />
            </div>

            <Button
              type="button"
              variant="outline"
              fullWidth
              size="lg"
              loading={isGoogleLoading}
              onClick={handleGoogleLogin}
              icon={
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4" />
                  <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853" />
                  <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4068 3.78409 7.83 3.96409 7.29V4.9581H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4522 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05" />
                  <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9254L15.0218 2.344C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9581L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335" />
                </svg>
              }
            >
              Entrar com Google
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
            Nao tem uma conta?{' '}
            <Link
              to="/register"
              className="font-medium text-[var(--color-brand)] transition-colors duration-150 hover:text-[var(--color-brand-hover)]"
            >
              Criar conta
            </Link>
          </p>
        </div>
      </animated.div>
    </div>
  )
}
